import { supabase } from './supabase';
import { Product, Review, Order, HealthArticle, Notice, Category, Coupon } from '../types';
import { devLog } from './dev-logger';

// Robust retry logic and error handling
interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  timeout?: number;
}

class DatabaseError extends Error {
  constructor(message: string, public originalError?: any) {
    super(message);
    this.name = 'DatabaseError';
  }
}

// Check network connection status
const isOnline = (): boolean => {
  return navigator.onLine;
};

// Check if error is retryable
const isRetryableError = (error: any): boolean => {
  if (!error) return false;
  
  const retryableMessages = [
    'fetch failed',
    'network error',
    'timeout',
    'connection',
    'ECONNRESET',
    'ENOTFOUND',
    'ETIMEDOUT',
    'server error',
    '503',
    '502',
    '504',
    'timeout',
    'api call timeout',
    'database connection timeout'
  ];
  
  const errorMessage = error.message?.toLowerCase() || '';
  const errorName = error.name?.toLowerCase() || '';
  
  // DatabaseError class timeout errors are always retryable
  if (error.name === 'DatabaseError' && errorMessage.includes('timeout')) {
    return true;
  }
  
  return retryableMessages.some(msg => 
    errorMessage.includes(msg) || errorName.includes(msg)
  );
};

// Robust API wrapper function
async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 2, // minimize retries
    baseDelay = 500, // shorter retry interval
    maxDelay = 2000, // shorter max delay
    timeout = 5000 // timeout shortened to 5s
  } = options;

  let lastError: any;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Check online status
      if (!isOnline()) {
        throw new DatabaseError('Please check your internet connection', { code: 'OFFLINE' });
      }

      console.log(`API call attempt ${attempt}/${maxRetries}`);

      // Timeout wrapper
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new DatabaseError(`API call timeout (${timeout}ms)`, { code: 'TIMEOUT' }));
        }, timeout);
      });

      // Actual API call
      const result = await Promise.race([
        operation(),
        timeoutPromise
      ]);

      console.log(`API call succeeded (attempt ${attempt})`);
      return result;

    } catch (error) {
      lastError = error;
      console.error(`API call failed (attempt ${attempt}):`, error);

      // Non-retryable errors throw immediately
      if (!isRetryableError(error)) {
        console.error('Non-retryable error:', error);
        const errorMessage = error instanceof Error ? error.message : 'A database error occurred';
        throw new DatabaseError(errorMessage, error);
      }

      // Throw error on last attempt
      if (attempt === maxRetries) {
        console.error(`All retries failed (${maxRetries} attempts)`);
        throw new DatabaseError(
          `Database connection failed. All ${maxRetries} retries exhausted.`,
          lastError
        );
      }

      // Exponential backoff with jitter
      const delay = Math.min(
        baseDelay * Math.pow(2, attempt - 1) + Math.random() * 1000,
        maxDelay
      );
      
      console.log(`Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

// Cart item type (same as CartContext)
interface CartItem {
  id: string;
  user_id: string;
  product_id: string;
  product_name: string;
  manufacturer: string;
  price: number;
  original_price: number;
  quantity: number;
  pill_quantity: number;
  image?: string;
  created_at: string;
  updated_at: string;
}

// Type transformation helper functions
const transformCategory = (item: any): Category => ({
  id: item.id,
  name: item.name,
  parentId: item.parent_id,
  level: item.level,
  displayOrder: item.display_order
});

const transformCartItem = (item: any) => ({
  id: item.id,
  productId: item.product_id,
  name: item.product_name,
  manufacturer: item.manufacturer,
  price: parseFloat(item.price),
  originalPrice: parseFloat(item.original_price),
  quantity: item.quantity,
  pillQuantity: item.pill_quantity,
  image: item.image,
  addedAt: item.created_at,
  discount: parseFloat(item.original_price) - parseFloat(item.price),
  stock: 999 // Default stock (should be joined from products table)
});

const transformProduct = (item: any): Product => {
  // Nested variants + prices come from the PostgREST select below.
  // Sort variants by display_order, sort prices within each variant by quantity.
  const rawVariants = Array.isArray(item.variants) ? item.variants : [];
  const variants = rawVariants
    .map((v: any) => ({
      id: v.id,
      product_id: v.product_id,
      strength_label: v.strength_label,
      strength_mg: v.strength_mg != null ? Number(v.strength_mg) : null,
      display_order: v.display_order || 0,
      sku: v.sku,
      image: v.image,
      is_default: !!v.is_default,
      prices: Array.isArray(v.prices)
        ? v.prices
            .map((p: any) => ({
              id: p.id,
              variant_id: p.variant_id,
              quantity: p.quantity,
              original_price: parseFloat(p.original_price) || 0,
              sale_price: parseFloat(p.sale_price) || 0,
              discount_rate: p.discount_rate || 0,
              created_at: p.created_at,
              updated_at: p.updated_at,
            }))
            .sort((a: any, b: any) => a.quantity - b.quantity)
        : [],
      created_at: v.created_at,
      updated_at: v.updated_at,
    }))
    .sort((a: any, b: any) => {
      if (a.display_order !== b.display_order) return a.display_order - b.display_order;
      return (a.strength_mg ?? 0) - (b.strength_mg ?? 0);
    });

  return {
    id: item.id,
    name: item.name,
    manufacturer: item.manufacturer,
    category_id: item.category_id,
    category_name: item.category_name || '',
    categories: Array.isArray(item.categories) ? item.categories : [],
    price: item.price,
    pill_quantity: item.pill_quantity,
    description: item.description,
    ingredients: item.ingredients,
    usage_instructions: item.usage_instructions,
    warnings: item.warnings,
    storage_instructions: item.storage_instructions,
    image: item.image,
    stock: item.stock,
    is_active: item.is_active,
    rating: parseFloat(item.rating) || 0,
    review_count: item.review_count || 0,
    sort_order: item.sort_order ?? 999,
    is_featured: !!item.is_featured,
    created_at: item.created_at,
    updated_at: item.updated_at,
    variants,
  };
};

const transformReview = (item: any): Review => {
  // Function to find and mask name/email from available data
  const getDisplayName = () => {
    // 1. When proper name exists
    if (item.user_name && item.user_name !== 'NULL' && item.user_name.trim() !== '') {
      const name = item.user_name.trim();
      if (name.length <= 2) return name.charAt(0) + '*';
      return name.charAt(0) + '*'.repeat(name.length - 2) + name.slice(-1);
    }
    
    // 2. No name, but email-format data exists (in user_email or user_id)
    // CSV data often has emails in user_id field, so check both
    const emailCandidate = item.user_email || item.user_id || '';
    
    if (emailCandidate.includes('@')) {
      const [id, domain] = emailCandidate.split('@');
      // Show first 3 chars and mask the rest (e.g.: roka101... -> rok***@gmail.com)
      const visibleId = id.slice(0, 3);
      return `${visibleId}***@${domain}`;
    }

    // 3. No info available at all
    return 'Anonymous';
  };

  return {
    id: item.id,
    productId: item.product_id,
    productName: '', 
    userId: item.user_id,
    userName: getDisplayName(), // Masks email even if stored in user_id
    userEmail: item.user_email || (item.user_id?.includes('@') ? item.user_id : ''), 
    rating: item.rating,
    title: item.title || '',
    content: item.content,
    status: item.status || (item.is_verified ? 'approved' : 'pending'),
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    adminReply: item.admin_reply,
    reviewedBy: item.reviewed_by,
    reviewedAt: item.reviewed_at,
    is_verified: item.is_verified, // Underscore field that caused error
    images: item.images,
    initialViews: item.initial_views
  };
};

// Category API
export const categoryApi = {
  getAll: async (): Promise<Category[]> => {
    // Simplified: retry logic removed (managed by SupabaseDataContext)
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('level', { ascending: true })
      .order('display_order', { ascending: true })
      .order('id', { ascending: true });

    if (error) {
      throw new DatabaseError(`Failed to load categories: ${error.message}`, error);
    }

    return data?.map(transformCategory) || [];
  },

  create: async (name: string, parentId?: number): Promise<Category | null> => {
    let level = 0;
    if (parentId) {
      const { data: parentData } = await supabase
        .from('categories')
        .select('level')
        .eq('id', parentId)
        .single();
      level = (parentData?.level || 0) + 1;
    }

    const { data, error } = await supabase
      .from('categories')
      .insert([{ name, parent_id: parentId, level }])
      .select()
      .single();

    if (error) {
      console.error('Error creating category:', error);
      return null;
    }

    return transformCategory(data);
  },

  update: async (id: number, updates: Partial<Category>): Promise<Category | null> => {
    console.log('🔄 Updating category:', { id, updates });
    
    const { data, error } = await supabase
      .from('categories')
      .update({
        name: updates.name,
        parent_id: updates.parentId,
        level: updates.level
      })
      .eq('id', id)
      .select();

    if (error) {
      console.error('❌ Error updating category:', error);
      return null;
    }

    console.log('✅ Category update response:', data);
    
    if (!data || data.length === 0) {
      console.error('❌ No rows updated - category ID might not exist:', id);
      return null;
    }

    return transformCategory(data[0]);
  },

  delete: async (id: number): Promise<boolean> => {
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting category:', error);
      return false;
    }

    return true;
  }
};

// Product API
export const productApi = {
  getAll: async (): Promise<Product[]> => {
    // Slim listing query — excludes heavy text fields (877 KB / 91% of row data).
    // ProductDetail calls getById() separately for full content.
    const { data, error } = await supabase
      .from('products')
      .select(`
        id, name, manufacturer, categories, category_id,
        price, original_price, pill_quantity,
        image, stock, is_active, rating, review_count,
        sort_order, is_featured, created_at, updated_at,
        category_join:categories(name),
        variants:product_variants(
          id, product_id, strength_label, strength_mg,
          display_order, sku, image, is_default,
          prices:product_prices(
            id, variant_id, quantity, original_price, sale_price, discount_rate
          )
        )
      `)
      .eq('is_active', true);

    if (error) {
      throw new DatabaseError(`Failed to load products: ${error.message}`, error);
    }

    return data?.map((item: any) => transformProduct({
      ...item,
      category_name: item.category_join?.name || ''
    })) || [];
  },

  getById: async (id: string): Promise<Product | null> => {
    return withRetry(async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          category_join:categories(
            id,
            name
          ),
          variants:product_variants(
            *,
            prices:product_prices(*)
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        throw new DatabaseError(`Failed to fetch product: ${error.message}`, error);
      }

      return transformProduct({
        ...data,
        category_name: data.category_join?.name || ''
      });
    }, { timeout: 10000 });
  },

  create: async (productData: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<Product | null> => {
    const { data, error } = await supabase
      .from('products')
      .insert([{
        id: crypto.randomUUID(),
        name: productData.name,
        manufacturer: productData.manufacturer,
        category_id: productData.category_id,
        price: productData.price,
        pill_quantity: productData.pill_quantity,
        description: productData.description,
        ingredients: productData.ingredients,
        usage_instructions: productData.usage_instructions,
        warnings: productData.warnings,
        storage_instructions: productData.storage_instructions,
        image: productData.image,
        stock: productData.stock,
        is_active: productData.is_active
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating product:', error);
      return null;
    }

    return transformProduct(data);
  },

  update: async (id: string, updates: Partial<Product>): Promise<Product | null> => {
    const updateData: any = {};
    
    // ✅ Fix: Use !== undefined for all fields to allow empty strings
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.manufacturer !== undefined) updateData.manufacturer = updates.manufacturer;
    if (updates.category_id !== undefined) updateData.category_id = updates.category_id;
    if (updates.price !== undefined) updateData.price = updates.price;
    if (updates.pill_quantity !== undefined) updateData.pill_quantity = updates.pill_quantity;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.ingredients !== undefined) updateData.ingredients = updates.ingredients;
    if (updates.usage_instructions !== undefined) updateData.usage_instructions = updates.usage_instructions;
    if (updates.warnings !== undefined) updateData.warnings = updates.warnings;
    if (updates.storage_instructions !== undefined) updateData.storage_instructions = updates.storage_instructions;
    if (updates.image !== undefined) updateData.image = updates.image;
    if (updates.stock !== undefined) updateData.stock = updates.stock;
    if (updates.is_active !== undefined) updateData.is_active = updates.is_active;
    if (updates.rating !== undefined) updateData.rating = updates.rating;
    if (updates.review_count !== undefined) updateData.review_count = updates.review_count;
    
    console.log('🚀 DEBUG: productApi.update - updateData:', updateData);
    console.log('🚀 DEBUG: productApi.update - product ID:', id);

    const { data, error } = await supabase
      .from('products')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('🚀 DEBUG: Supabase update failed:', error);
      console.error('🚀 DEBUG: Error code:', error.code);
      console.error('🚀 DEBUG: Error message:', error.message);
      console.error('🚀 DEBUG: Error details:', error.details);
      console.error('🚀 DEBUG: Update data that failed:', updateData);
      console.error('🚀 DEBUG: Target product ID:', id);
      return null;
    }

    console.log('✅ DEBUG: Supabase update successful');
    console.log('🚀 DEBUG: Updated data from Supabase:', data);
    const transformedProduct = transformProduct(data);
    console.log('🚀 DEBUG: Transformed product:', transformedProduct);
    return transformedProduct;
  },

  delete: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('products')
      .update({ is_active: false })
      .eq('id', id);

    if (error) {
      console.error('Error deleting product:', error);
      return false;
    }

    return true;
  }
};

// Review API
export const reviewApi = {
  getAll: async (): Promise<Review[]> => {
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching reviews:', error);
      return [];
    }

    return data?.map((item: any) => transformReview(item)) || [];
  },

  create: async (reviewData: Omit<Review, 'id' | 'createdAt' | 'updatedAt'>): Promise<Review | null> => {
    const { data, error } = await supabase
      .from('reviews')
      .insert([{
        product_id: reviewData.product_id || reviewData.productId,
        user_id: reviewData.user_id || reviewData.userId,
        rating: reviewData.rating,
        title: reviewData.title,
        content: reviewData.content,
        is_verified: (reviewData.status || reviewData.is_verified) === 'approved' || reviewData.is_verified === true
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating review:', error);
      return null;
    }

    return transformReview(data);
  },

  update: async (id: string, updates: Partial<Review>): Promise<Review | null> => {
    const updateData: any = {};
    
    if (updates.rating) updateData.rating = updates.rating;
    if (updates.title) updateData.title = updates.title;
    if (updates.content) updateData.content = updates.content;
    if (updates.status) updateData.is_verified = updates.status === 'approved';

    const { data, error } = await supabase
      .from('reviews')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating review:', error);
      return null;
    }

    return transformReview(data);
  },

  delete: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting review:', error);
      return false;
    }

    return true;
  },

  // Additional functions used by AdminContext
  getAllReviews: async () => {
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching reviews:', error);
      return [];
    }

    return data?.map((item: any) => transformReview(item)) || [];
  },

  addReview: async (reviewData: {
    product_id: string;
    user_id: string;
    user_name?: string;
    user_email?: string;
    rating: number;
    title: string;
    content: string;
    images?: string[] | null; // Image URL array
    status?: 'pending' | 'approved' | 'rejected';
    is_verified?: boolean;
  }) => {
    // Random initial view count (500~1500)
    const initialViews = Math.floor(Math.random() * 1000) + 500;

    const { data, error } = await supabase
      .from('reviews')
      .insert({
        product_id: reviewData.product_id,
        user_id: reviewData.user_id,
        user_name: reviewData.user_name,
        user_email: reviewData.user_email,
        rating: reviewData.rating,
        title: reviewData.title,
        content: reviewData.content,
        images: reviewData.images || null, // Store images
        status: reviewData.status || 'pending',
        is_verified: reviewData.is_verified || false,
        initial_views: initialViews
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding review:', error);
      return null;
    }

    return transformReview(data);
  },

  updateReviewStatus: async (reviewId: string, status: 'approved' | 'rejected' | 'pending') => {
    const { error } = await supabase
      .from('reviews')
      .update({
        status: status,
        is_verified: status === 'approved', // Backward compatibility (supports legacy dummy reviews)
        updated_at: new Date().toISOString()
      })
      .eq('id', reviewId);

    if (error) {
      console.error('❌ Error updating review status:', error);
      throw error; // Throw so AdminContext can detect the error
    }

    // Points are handled by AdminContext.approveReview() (no duplication)
    console.log(`✅ Review status updated: ${reviewId} → ${status}`);
    return true;
  },

  addAdminReply: async (reviewId: string, reply: string) => {
    const { data, error } = await supabase
      .from('reviews')
      .update({
        admin_reply: reply,
        updated_at: new Date().toISOString()
      })
      .eq('id', reviewId)
      .select()
      .single();

    if (error) {
      console.error('Error adding admin reply:', error);
      return null;
    }

    return transformReview(data);
  }
};

// Health Magazine API
export const healthArticleApi = {
  // Public site: published articles only
  getPublished: async (): Promise<HealthArticle[]> => {
    const { data, error } = await supabase
      .from('health_articles')
      .select('*')
      .eq('is_published', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching published health articles:', error);
      return [];
    }

    return data?.map((item: any) => ({
      id: item.id,
      title: item.title,
      content: item.content,
      author: item.author,
      category: item.category || '',
      tags: item.tags || [],
      summary: item.summary || '',
      featuredImage: item.thumbnail,
      thumbnail: item.thumbnail,
      isPublished: item.is_published,
      publishedAt: item.published_at,
      views: item.views,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      slug: item.slug,
      metaDescription: item.meta_description,
      readTimeMinutes: item.read_time_minutes,
      featured: item.featured
    })) || [];
  },

  // Admin: all articles (published + drafts)
  getAll: async (): Promise<HealthArticle[]> => {
    const { data, error } = await supabase
      .from('health_articles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching health articles:', error);
      return [];
    }

    // DEBUG: Log raw Supabase data
    console.log('healthArticleApi.getAll - Raw Supabase data:');
    data?.forEach((item: any, index: number) => {
      console.log(`${index + 1}. ${item.title}`);
      console.log('   - All fields:', Object.keys(item));
      console.log('   - is_published:', item.is_published, typeof item.is_published);
      console.log('   - isPublished:', item.isPublished, typeof item.isPublished);
      console.log('   - published:', item.published, typeof item.published);
    });

    return data?.map((item: any) => ({
      id: item.id,
      title: item.title,
      content: item.content,
      author: item.author,
      category: item.category || '',
      tags: item.tags || [],
      summary: item.summary || '',
      featuredImage: item.thumbnail,
      thumbnail: item.thumbnail,
      isPublished: item.is_published,
      publishedAt: item.published_at,
      views: item.views,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      slug: item.slug,
      metaDescription: item.meta_description,
      readTimeMinutes: item.read_time_minutes,
      featured: item.featured
    })) || [];
  },

  create: async (articleData: any): Promise<HealthArticle | null> => {
    console.log('🚨 DEBUG healthArticleApi.create:');
    console.log('- Received articleData:', articleData);
    console.log('- articleData.isPublished:', articleData.isPublished);
    console.log('- articleData.is_published:', articleData.is_published);

    const { data, error } = await supabase
      .from('health_articles')
      .insert([{
        title: articleData.title,
        content: articleData.content,
        summary: articleData.summary,
        author: articleData.author,
        category: articleData.category,
        tags: articleData.tags,
        thumbnail: articleData.featuredImage || articleData.thumbnail,
        is_published: articleData.is_published !== undefined ? articleData.is_published : articleData.isPublished,
        read_time_minutes: articleData.read_time_minutes || articleData.readTimeMinutes || 5,
        meta_description: articleData.meta_description || articleData.metaDescription,
        slug: articleData.slug,
        featured: articleData.featured
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating health article:', error);
      return null;
    }

    return {
      id: data.id,
      title: data.title,
      content: data.content,
      author: data.author,
      category: data.category || '',
      tags: data.tags || [],
      summary: data.summary || '',
      featuredImage: data.thumbnail,
      thumbnail: data.thumbnail,
      isPublished: data.is_published,
      publishedAt: data.published_at,
      views: data.views,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      slug: data.slug,
      metaDescription: data.meta_description,
      readTimeMinutes: data.read_time_minutes,
      featured: data.featured
    };
  },

  update: async (id: string, updates: any): Promise<HealthArticle | null> => {
    // Data already transformed by HealthArticleContext, use as-is
    const updateData: any = { ...updates };

    console.log('🚨 DEBUG healthArticleApi.update:');
    console.log('- Article ID:', id);
    console.log('- Update data being sent to Supabase:', updateData);
    console.log('- Original updates:', updates);

    const { data, error } = await supabase
      .from('health_articles')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating health article:', error);
      return null;
    }

    return {
      id: data.id,
      title: data.title,
      content: data.content,
      author: data.author,
      category: data.category || '',
      tags: data.tags || [],
      summary: data.summary || '',
      featuredImage: data.thumbnail,
      thumbnail: data.thumbnail,
      isPublished: data.is_published,
      publishedAt: data.published_at,
      views: data.views,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      slug: data.slug,
      metaDescription: data.meta_description,
      readTimeMinutes: data.read_time_minutes,
      featured: data.featured
    };
  },

  delete: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('health_articles')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting health article:', error);
      return false;
    }

    return true;
  }
};

// Notice API
export const noticeApi = {
  getAll: async (): Promise<Notice[]> => {
    const { data, error } = await supabase
      .from('notices')
      .select('*')
      .eq('is_published', true)
      .order('is_pinned', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching notices:', error);
      return [];
    }

    return data?.map((item: any) => ({
      id: item.id,
      title: item.title,
      content: item.content,
      author: item.author,
      isImportant: item.is_pinned || false,
      views: item.views,
      createdAt: item.created_at,
      updatedAt: item.updated_at
    })) || [];
  },

  create: async (noticeData: Omit<Notice, 'id' | 'views' | 'createdAt' | 'updatedAt'>): Promise<Notice | null> => {
    const { data, error } = await supabase
      .from('notices')
      .insert([{
        title: noticeData.title,
        content: noticeData.content,
        type: 'general',
        is_pinned: noticeData.isImportant,
        is_published: true,
        author: noticeData.author
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating notice:', error);
      return null;
    }

    return {
      id: data.id,
      title: data.title,
      content: data.content,
      author: data.author,
      isImportant: data.is_pinned || false,
      views: data.views,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  update: async (id: string, updates: Partial<Notice>): Promise<Notice | null> => {
    const updateData: any = {};
    
    if (updates.title) updateData.title = updates.title;
    if (updates.content) updateData.content = updates.content;
    if (updates.isImportant !== undefined) updateData.is_pinned = updates.isImportant;
    if (updates.author) updateData.author = updates.author;

    const { data, error } = await supabase
      .from('notices')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating notice:', error);
      return null;
    }

    return {
      id: data.id,
      title: data.title,
      content: data.content,
      author: data.author,
      isImportant: data.is_pinned || false,
      views: data.views,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  delete: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('notices')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting notice:', error);
      return false;
    }

    return true;
  }
};

// Order API
export const orderApi = {
  /**
   * Get orders for the currently logged-in user (using RLS)
   */
  getMyOrders: async (): Promise<Order[]> => {
    try {
      // Check currently authenticated user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('Authentication error:', authError);
        return [];
      }
      
      if (!user) {
        console.error('No logged-in user');
        return [];
      }

      // RLS policy automatically filters by user_id.
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(*)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Order fetch error:', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Data transformation
      const transformedOrders = (data || []).map((item: any) => ({
        id: item.id,
        userId: item.user_id,
        userName: item.user_name,
        userEmail: item.user_email,
        userPhone: item.user_phone,
        orderNumber: item.order_number,
        items: (item.order_items || []).map((orderItem: any) => ({
          productId: orderItem.product_id,
          productName: orderItem.product_name,
          quantity: orderItem.quantity,
          price: orderItem.price,
          pillQuantity: orderItem.pill_quantity,
          image: orderItem.image,
          manufacturer: orderItem.manufacturer
        })),
        subtotal: item.subtotal,
        totalAmount: item.total_amount,
        shippingFee: item.shipping_fee || 0,
        discountAmount: item.discount || 0,
        status: item.status,
        paymentStatus: item.payment_status,
        paymentMethod: item.payment_method,
        shippingAddress: item.shipping_address,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        trackingNumber: item.tracking_number,
        shippingCompany: item.shipping_company
      }));

      return transformedOrders;
    } catch (error) {
      console.error('Order fetch exception:', error);
      return [];
    }
  },

  /**
   * Admin function to retrieve all orders.
   * Calls 'admin-api' Edge Function to bypass RLS policies.
   */
  getAll: async (): Promise<Order[]> => {
    try {
      console.log('🔄 Calling Edge Function: admin-api (get-all-orders)');
      const { data, error } = await supabase.functions.invoke('admin-api', {
        body: { resource: 'get-all-orders' },
      });

      if (error) throw error;
      
      // Transform Edge Function data to match frontend types
      return (data || []).map((item: any) => ({
        id: item.id,
        userId: item.user_id,
        userName: item.user_name,
        userEmail: item.user_email,
        userPhone: item.user_phone,
        orderNumber: item.order_number,
        items: (item.order_items || []).map((orderItem: any) => ({
          productId: orderItem.product_id,
          productName: orderItem.product_name,
          quantity: orderItem.quantity,
          price: orderItem.price,
          pillQuantity: orderItem.pill_quantity,
          image: orderItem.image,
          manufacturer: orderItem.manufacturer
        })),
        subtotal: item.subtotal,
        totalAmount: item.total_amount,
        shippingFee: item.shipping_fee || 0,
        discountAmount: item.discount || 0,
        status: item.status,
        paymentStatus: item.payment_status,
        paymentMethod: item.payment_method,
        shippingAddress: item.shipping_address,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        trackingNumber: item.tracking_number,
        shippingCompany: item.shipping_company
      }));
    } catch (error) {
      console.error('❌ Error invoking admin-api (get-all-orders):', error);
      return [];
    }
  },

  getById: async (id: string): Promise<Order | null> => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(*)
        `)
        .eq('id', id)
        .single(); // Use single() to get exactly one result.

      if (error) {
        throw new DatabaseError(`Failed to fetch order (ID: ${id}): ${error.message}`, error);
      }
      
      if (!data) return null;

      // Apply the same data transformation logic used in getAll.
      return {
        id: data.id,
        userId: data.user_id,
        userName: data.user_name,
        userEmail: data.user_email,
        userPhone: data.user_phone,
        orderNumber: data.order_number,
        items: data.order_items?.map((orderItem: any) => ({
          productId: orderItem.product_id,
          productName: orderItem.product_name,
          quantity: orderItem.quantity,
          price: orderItem.price,
          pillQuantity: orderItem.pill_quantity,
          image: orderItem.image,
          manufacturer: orderItem.manufacturer
        })) || [],
        subtotal: data.subtotal,
        totalAmount: data.total_amount,
        shippingFee: data.shipping_fee || 0,
        discountAmount: data.discount || 0,
        status: data.status,
        paymentStatus: data.payment_status,
        paymentMethod: data.payment_method,
        shippingAddress: data.shipping_address,
        trackingNumber: data.tracking_number,
        shippingCompany: data.shipping_company,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (error) {
      console.error(`Error in orderApi.getById for order ${id}:`, error);
      return null;
    }
  },



  /**
   * Admin function to update order status.
   * Calls Edge Function.
   */
  updateStatus: async (orderId: string, status: string): Promise<boolean> => {
    try {
      const { error } = await supabase.functions.invoke('admin-api', {
        body: { 
          resource: 'update-order-status',
          payload: { orderId, status }
        },
      });
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('❌ Error invoking admin-api (update-order-status):', error);
      return false;
    }
  },

  /**
   * Admin function to add tracking number.
   * Calls Edge Function.
   */
  addTrackingNumber: async (orderId: string, trackingNumber: string, shippingCompany: string): Promise<boolean> => {
    try {
      const { error } = await supabase.functions.invoke('admin-api', {
        body: {
          resource: 'add-tracking-number',
          payload: { orderId, trackingNumber, shippingCompany }
        }
      });
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('❌ Error invoking admin-api (add-tracking-number):', error);
      return false;
    }
  },

  // Permanently delete order (delete order_items + orders from DB)
  delete: async (id: string): Promise<boolean> => {
    try {
      console.log('Starting order deletion:', id);

      // 1. Delete order_items first (due to FK constraint)
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', id);

      if (itemsError) {
        console.error('order_items deletion failed:', itemsError);
        return false;
      }

      // 2. Then delete orders
      const { error: orderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', id);

      if (orderError) {
        console.error('orders deletion failed:', orderError);
        return false;
      }

      console.log('Order deleted:', id);
      return true;

    } catch (error) {
      console.error('Order deletion exception:', error);
      return false;
    }
  },

  updatePaymentStatus: async (id: string, paymentStatus: string): Promise<boolean> => {
    const { error } = await supabase
      .from('orders')
      .update({ payment_status: paymentStatus })
      .eq('id', id);

    if (error) {
      console.error('Error updating payment status:', error);
      return false;
    }

    return true;
  },

  /**
   * User function to cancel their own order.
   * RLS policy ensures only the user's own orders can be cancelled.
   * Cancellation condition: Only 'payment_pending' status (cannot cancel after payment confirmation)
   */
  cancelMyOrder: async (orderId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // 1. Verify currently authenticated user
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        return { success: false, error: 'Please log in.' };
      }

      // 2. Get order info (RLS allows only own orders)
      const { data: order, error: fetchError } = await supabase
        .from('orders')
        .select('id, status, user_id')
        .eq('id', orderId)
        .single();

      if (fetchError || !order) {
        return { success: false, error: 'Order not found.' };
      }

      // 3. Check if order is cancellable
      const cancellableStatuses = ['payment_pending'];
      if (!cancellableStatuses.includes(order.status)) {
        if (order.status === 'payment_confirmed') {
          return { success: false, error: 'Payment-confirmed orders cannot be cancelled. Please contact support.' };
        } else if (order.status === 'shipping') {
          return { success: false, error: 'Shipping orders cannot be cancelled. Please contact support.' };
        } else if (order.status === 'delivered') {
          return { success: false, error: 'Delivered orders cannot be cancelled. Please use the return process.' };
        } else if (order.status === 'cancelled') {
          return { success: false, error: 'This order is already cancelled.' };
        }
        return { success: false, error: 'This order cannot be cancelled.' };
      }

      // 4. Change order status to 'cancelled' (RLS ensures only own orders)
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId)
        .eq('user_id', user.id); // Double check: only modify own orders

      if (updateError) {
        console.error('Order cancellation failed:', updateError);
        return { success: false, error: 'An error occurred while cancelling the order.' };
      }

      // 5. Restore coupons linked to this order. Delegates to the
      //    restore_coupons_on_order_cancel RPC which enforces that the
      //    order is actually cancelled + owned by the caller, since the
      //    user_coupons table no longer allows direct UPDATE from clients.
      try {
        const { data, error: restoreError } = await supabase.rpc('restore_coupons_on_order_cancel', {
          p_order_id: orderId,
        });
        if (restoreError) {
          console.error('Coupon restoration RPC error:', restoreError);
        } else if ((data as any)?.success) {
          const restored = (data as any).restored ?? 0;
          if (restored > 0) console.log(`${restored} coupon(s) restored`);
        } else {
          console.warn('Coupon restoration skipped:', (data as any)?.message);
        }
      } catch (couponError) {
        console.error('Coupon restoration failed (order cancellation completed):', couponError);
      }

      // 6. Restore points. Filter by order_id + spend-type used by
      //    spend_points ('order_use') — the previous filter used the
      //    non-existent `reference_id` column and a stale 'use' type,
      //    so the refund never fired.
      try {
        const { data: pointUsage } = await supabase
          .from('point_transactions')
          .select('amount')
          .eq('user_id', user.id)
          .eq('order_id', orderId)
          .eq('type', 'order_use');

        if (pointUsage && pointUsage.length > 0) {
          const totalUsed = pointUsage.reduce((sum: number, t: any) => sum + Math.abs(t.amount), 0);
          if (totalUsed > 0) {
            await pointApi.addPoints(
              user.id,
              totalUsed,
              'order_cancel_refund',
              `Order cancellation refund (order: ${orderId.slice(0, 8)}...)`,
              orderId
            );
            console.log(`${totalUsed} points restored`);
          }
        }
      } catch (pointError) {
        console.error('Points restoration failed (order cancellation completed):', pointError);
      }

      console.log('Order cancelled:', orderId);
      return { success: true };

    } catch (error) {
      console.error('Order cancellation exception:', error);
      return { success: false, error: 'An error occurred while cancelling the order.' };
    }
  },

  // WordPress-style trash features
  
  // Move to trash (change status to 'trash')
  moveToTrash: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('orders')
      .update({ 
        status: 'trash',
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) {
      console.error('Error moving order to trash:', error);
      return false;
    }

    console.log('Order moved to trash:', id);
    return true;
  },

  // Restore from trash (revert to previous status)
  restoreFromTrash: async (id: string, previousStatus: string = 'payment_pending'): Promise<boolean> => {
    const { error } = await supabase
      .from('orders')
      .update({ 
        status: previousStatus,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) {
      console.error('Error restoring order from trash:', error);
      return false;
    }

    console.log('Order restored from trash:', id);
    return true;
  },

  // Get trash orders
  getTrashOrders: async (): Promise<Order[]> => {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items(*)
      `)
      .eq('status', 'trash')
      .order('updated_at', { ascending: false }); // Sort by trash move time

    if (error) {
      console.error('Error fetching trash orders:', error);
      return [];
    }

    return data?.map((item: any) => ({
      id: item.id,
      userId: item.user_id,
      userName: item.customer_name || item.user_name, // Unified DB field name
      userEmail: item.user_email,
      orderNumber: item.order_number,
      items: item.order_items?.map((orderItem: any) => ({
        productId: orderItem.product_id,
        productName: orderItem.product_name,
        quantity: orderItem.quantity,
        price: orderItem.total_price, // Use actual total price
        pillQuantity: orderItem.pill_quantity
      })) || [],
      totalAmount: item.total_amount,
      shippingFee: item.shipping_fee || 0,
      discountAmount: item.discount || 0,
      status: item.status,
      paymentStatus: item.payment_status,
      shippingAddress: item.shipping_address_full || 'No address info',
      createdAt: item.created_at,
      updatedAt: item.updated_at
    })) || [];
  },

  // Get active orders (excluding trash)
  getActiveOrders: async (): Promise<Order[]> => {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items(*)
      `)
      .neq('status', 'trash') // Exclude trash
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching active orders:', error);
      return [];
    }

    return data?.map((item: any) => ({
      id: item.id,
      userId: item.user_id,
      userName: item.customer_name || item.user_name,
      userEmail: item.user_email,
      orderNumber: item.order_number,
      items: item.order_items?.map((orderItem: any) => ({
        productId: orderItem.product_id,
        productName: orderItem.product_name,
        quantity: orderItem.quantity,
        price: orderItem.total_price,
        pillQuantity: orderItem.pill_quantity
      })) || [],
      totalAmount: item.total_amount,
      shippingFee: item.shipping_fee || 0,
      discountAmount: item.discount || 0,
      status: item.status,
      paymentStatus: item.payment_status,
      shippingAddress: item.shipping_address_full || 'No address info',
      createdAt: item.created_at,
      updatedAt: item.updated_at
    })) || [];
  }
};

// Real-time subscription setup
export const setupRealtimeSubscriptions = (callbacks: {
  onProductsChange?: () => void;
  onOrdersChange?: () => void;
  onReviewsChange?: () => void;
  onCategoriesChange?: () => void;
}) => {
  const subscriptions: any[] = [];

  if (callbacks.onProductsChange) {
    const productsSub = supabase
      .channel('products-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'products' 
      }, callbacks.onProductsChange)
      .subscribe();
    subscriptions.push(productsSub);
  }

  if (callbacks.onOrdersChange) {
    const ordersSub = supabase
      .channel('orders-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'orders' 
      }, callbacks.onOrdersChange)
      .subscribe();
    subscriptions.push(ordersSub);
  }

  if (callbacks.onReviewsChange) {
    const reviewsSub = supabase
      .channel('reviews-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'reviews' 
      }, callbacks.onReviewsChange)
      .subscribe();
    subscriptions.push(reviewsSub);
  }

  if (callbacks.onCategoriesChange) {
    const categoriesSub = supabase
      .channel('categories-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'categories' 
      }, callbacks.onCategoriesChange)
      .subscribe();
    subscriptions.push(categoriesSub);
  }

  return () => {
    subscriptions.forEach(sub => supabase.removeChannel(sub));
  };
};

// Cart API
export const cartApi = {
  // Get user cart
  getUserCart: async (userId: string) => {
    const { data, error } = await supabase
      .from('cart_items')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching cart:', error);
      return [];
    }

    return data?.map(transformCartItem) || [];
  },

  // Add/update cart item
  addToCart: async (userId: string, productData: {
    productId: string;
    productName: string;
    manufacturer: string;
    price: number;
    originalPrice: number;
    quantity: number;
    pillQuantity: number;
    image?: string;
  }) => {
    const { data, error } = await supabase
      .from('cart_items')
      .upsert({
        user_id: userId,
        product_id: productData.productId,
        product_name: productData.productName,
        manufacturer: productData.manufacturer,
        price: productData.price,
        original_price: productData.originalPrice,
        quantity: productData.quantity,
        pill_quantity: productData.pillQuantity,
        image: productData.image
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding to cart:', error);
      return null;
    }

    return transformCartItem(data);
  },

  // Update cart item quantity
  updateQuantity: async (userId: string, productId: string, quantity: number) => {
    if (quantity <= 0) {
      return cartApi.removeFromCart(userId, productId);
    }

    const { data, error } = await supabase
      .from('cart_items')
      .update({ quantity })
      .eq('user_id', userId)
      .eq('product_id', productId)
      .select()
      .single();

    if (error) {
      console.error('Error updating cart quantity:', error);
      return null;
    }

    return transformCartItem(data);
  },

  // Remove cart item
  removeFromCart: async (userId: string, productId: string) => {
    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('user_id', userId)
      .eq('product_id', productId);

    if (error) {
      console.error('Error removing from cart:', error);
      return false;
    }

    return true;
  },

  // Clear cart
  clearCart: async (userId: string) => {
    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('user_id', userId);

    if (error) {
      console.error('Error clearing cart:', error);
      return false;
    }

    return true;
  }
};

// Wishlist API
export const wishlistApi = {
  // Get user wishlist
  getUserWishlist: async (userId: string) => {
    const { data, error } = await supabase
      .from('wishlist_items')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching wishlist:', error);
      return [];
    }

    return data?.map((item: any) => ({
      productId: item.product_id,
      productName: item.product_name,
      manufacturer: item.manufacturer,
      price: parseFloat(item.price),
      image: item.image,
      addedAt: item.created_at
    })) || [];
  },

  // Add to wishlist
  addToWishlist: async (userId: string, productData: {
    productId: string;
    productName: string;
    manufacturer: string;
    price: number;
    image?: string;
  }) => {
    const { data, error } = await supabase
      .from('wishlist_items')
      .upsert({
        user_id: userId,
        product_id: productData.productId,
        product_name: productData.productName,
        manufacturer: productData.manufacturer,
        price: productData.price,
        image: productData.image
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding to wishlist:', error);
      return null;
    }

    return data;
  },

  // Remove from wishlist
  removeFromWishlist: async (userId: string, productId: string) => {
    const { error } = await supabase
      .from('wishlist_items')
      .delete()
      .eq('user_id', userId)
      .eq('product_id', productId);

    if (error) {
      console.error('Error removing from wishlist:', error);
      return false;
    }

    return true;
  }
};

// Order creation API
export const orderCreationApi = {
  createOrder: async (userId: string, orderData: {
    items: any[];
    totalAmount: number;
    shippingFee: number;
    discount: number;
    shippingInfo: any;
    paymentMethod: string;
    couponId?: string; // New
    pointsUsed?: number; // New
  }) => {
    try {
      // Call PostgreSQL function (RPC) (v2: coupon/points transaction integrated)
      const { data, error } = await supabase
        .rpc('create_new_order', {
          order_items: orderData.items.map(item => ({ // Pass data matching function type definition
            product_id: item.productId,
            product_name: item.name,
            manufacturer: item.manufacturer,
            quantity: item.quantity,
            pill_quantity: item.pillQuantity,
            price: item.price,
            total_price: item.price * item.quantity,
            image: item.image
          })),
          total_amount: orderData.totalAmount,
          shipping_fee: orderData.shippingFee,
          discount_amount: orderData.discount,
          shipping_info: orderData.shippingInfo, // Pass as jsonb type
          payment_method: orderData.paymentMethod,
          // v2 params: RPC handles coupons/points atomically.
          // Floor points because p_points_used is integer in Postgres —
          // fractional leaks (e.g. pointsToUse capped by a decimal subtotal)
          // otherwise trip 22P02 "invalid input syntax for type integer".
          p_coupon_id: orderData.couponId || null,
          p_points_used: Math.max(0, Math.floor(orderData.pointsUsed || 0))
        });

      if (error) throw error; // Errors are caught here.

      // NOTE: cart is intentionally NOT cleared here. Standard e-commerce flow
      // clears cart only after payment succeeds (paid) — clearing on order
      // creation would empty the cart even if the user fails the invoice step
      // or never completes payment, leaving them with no recoverable state.
      // Cart-clear cascade lives in:
      //   - crypto-webhook (NOWPayments IPN 'finished')
      //   - check-crypto-status (poll-detected paid)
      //   - CryptoPayment.tsx (frontend paid detection — UX immediacy)

      // Fetch the full order + items so we can both return the order_number
      // and ship a confirmation email payload to send-order-notification.
      const { data: fresh } = await supabase
        .from('orders')
        .select('*, order_items(*)')
        .eq('id', data)
        .single();

      // Fire customer + admin confirmation emails (INSERT type sends both).
      // Non-fatal: order is already saved in DB regardless of email outcome.
      // Note: this runs from the browser with the user's JWT — see
      // docs/SECURITY-DEBT.md for the wrapper-function hardening TODO.
      if (fresh) {
        try {
          const items = ((fresh as any).order_items || []).map((oi: any) => ({
            name: oi.product_name,
            quantity: oi.quantity,
            pill_quantity: oi.pill_quantity,
            price: oi.price,
            image: oi.image,
            brand: oi.manufacturer,
            product_id: oi.product_id,
          }));
          const sa = (fresh as any).shipping_address;
          const fullAddr = sa && typeof sa === 'object'
            ? (sa.fullAddressEnglish
                ?? [sa.address, sa.city, sa.state, sa.zipCode, sa.country].filter(Boolean).join(', '))
            : null;
          const record = { ...fresh, shipping_address_full: fullAddr, items };
          delete (record as any).order_items;
          await supabase.functions.invoke('send-order-notification', {
            body: { record, type: 'INSERT' },
          });
        } catch (emailErr) {
          console.error('send-order-notification (INSERT) failed (non-fatal):', emailErr);
        }
      }

      return {
        success: true,
        orderId: data, // new_order_id returned by RPC
        orderNumber: (fresh as any)?.order_number ?? ''
      };

    } catch (error) {
      console.error('Error creating order:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
};


// Crypto Payment API
export const cryptoPaymentApi = {
  createInvoice: async (orderId: string, coin: string) => {
    // PR 5: return shape carries error code so the UI can route specific
    // failures (e.g. below_min_amount → bring user back to coin picker
    // instead of showing a dead-end "try again" button).
    const { data, error } = await supabase.functions.invoke('create-crypto-invoice', {
      body: { orderId, coin },
    });
    if (error) {
      console.error('Crypto invoice creation failed:', error);
      // Supabase SDK swallows the response body on non-2xx (FunctionsHttpError),
      // surfacing only "Edge Function returned a non-2xx status code". Recover
      // the real body via error.context (Response) so app-level codes like
      // below_min_amount route to the right UI branch instead of a dead end.
      const ctx = (error as any).context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          const body = await ctx.json();
          if (body && body.success === false) {
            return {
              ok: false as const,
              code: body.code || 'edge_error',
              message: body.error,
              minAmountUsd: body.minAmountUsd,
            };
          }
        } catch { /* body unreadable — fall through to network */ }
      }
      return { ok: false as const, code: 'network', message: error.message };
    }
    if (!data?.success) {
      console.error('Crypto invoice error:', data?.error);
      return {
        ok: false as const,
        code: data?.code || 'unknown',
        message: data?.error,
        minAmountUsd: data?.minAmountUsd,
      };
    }
    return { ok: true as const, ...data };
  },

  checkStatus: async (paymentId: string) => {
    const { data, error } = await supabase.functions.invoke('check-crypto-status', {
      body: { paymentId },
    });
    if (error) {
      console.error('Crypto status check failed:', error);
      return null;
    }
    return data;
  },

  cancel: async (paymentId: string) => {
    const { data, error } = await supabase.functions.invoke('cancel-crypto-invoice', {
      body: { paymentId },
    });
    if (error) {
      console.error('Crypto invoice cancel failed:', error);
      return { success: false, error: error.message ?? 'cancel failed' };
    }
    return data;
  },

  getByOrderId: async (orderId: string) => {
    const { data, error } = await supabase
      .from('crypto_payments')
      .select('*')
      .eq('order_id', orderId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error || !data) return null;
    return {
      paymentId: data.id,
      address: data.payment_address,
      amountCrypto: data.amount_crypto?.toString(),
      amountFiat: data.amount_fiat,
      coin: data.coin,
      network: data.network,
      expiresAt: data.expires_at,
      qrData: data.payment_address,
      providerInvoiceId: data.provider_invoice_id,
      status: data.status,
      confirmations: data.confirmations,
      requiredConfirmations: data.required_confirmations,
    };
  },
};

// Inquiry API
export const inquiryApi = {
  // Get all inquiries
  getAllInquiries: async () => {
    const { data, error } = await supabase
      .from('inquiries')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching inquiries:', error);
      return [];
    }

    return data?.map((item: any) => ({
      id: item.id,
      userId: item.user_id,
      userName: item.user_name,
      userEmail: item.user_email,
      category: item.category,
      title: item.title,
      content: item.content,
      orderNumber: item.order_number,
      status: item.status,
      adminReply: item.admin_reply,
      answeredBy: item.answered_by,
      answeredAt: item.answered_at,
      createdAt: item.created_at,
      updatedAt: item.updated_at
    })) || [];
  },

  // Add inquiry
  addInquiry: async (inquiryData: {
    userId: string;
    userName: string;
    userEmail: string;
    category: string;
    title: string;
    content: string;
    orderNumber?: string;
  }) => {
    const { data, error } = await supabase
      .from('inquiries')
      .insert({
        user_id: inquiryData.userId,
        user_name: inquiryData.userName,
        user_email: inquiryData.userEmail,
        category: inquiryData.category,
        title: inquiryData.title,
        content: inquiryData.content,
        order_number: inquiryData.orderNumber,
        status: 'pending'
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding inquiry:', error);
      throw error;
    }

    return {
      id: data.id,
      userId: data.user_id,
      userName: data.user_name,
      userEmail: data.user_email,
      category: data.category,
      title: data.title,
      content: data.content,
      orderNumber: data.order_number,
      status: data.status,
      adminReply: data.admin_reply,
      answeredBy: data.answered_by,
      answeredAt: data.answered_at,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  // Add admin response
  addAdminResponse: async (inquiryId: string, response: string, answeredBy?: string) => {
    const { data, error } = await supabase
      .from('inquiries')
      .update({
        admin_reply: response,
        answered_by: answeredBy || 'Admin',
        answered_at: new Date().toISOString(),
        status: 'answered'
      })
      .eq('id', inquiryId)
      .select()
      .single();

    if (error) {
      console.error('Error adding admin response:', error);
      throw error;
    }

    return {
      id: data.id,
      userId: data.user_id,
      userName: data.user_name,
      userEmail: data.user_email,
      category: data.category,
      title: data.title,
      content: data.content,
      orderNumber: data.order_number,
      status: data.status,
      adminReply: data.admin_reply,
      answeredBy: data.answered_by,
      answeredAt: data.answered_at,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  // Update inquiry status
  updateInquiryStatus: async (inquiryId: string, status: string) => {
    const { data, error } = await supabase
      .from('inquiries')
      .update({
        status: status,
        updated_at: new Date().toISOString()
      })
      .eq('id', inquiryId)
      .select()
      .single();

    if (error) {
      console.error('Error updating inquiry status:', error);
      return null;
    }

    return data;
  }
};

// 📷 Supabase Storage API for Image Management
export const storageApi = {
  // Upload image
  uploadImage: async (file: File, bucket: string = 'product-images', folder?: string): Promise<string | null> => {
    try {
      // Generate filename (timestamp + original name)
      const timestamp = new Date().getTime();
      const fileName = `${timestamp}-${file.name}`;
      const filePath = folder ? `${folder}/${fileName}` : fileName;

      console.log(`🔄 Uploading image to ${bucket}/${filePath}`);

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false // Error if file with same name exists
        });

      if (error) {
        console.error('❌ Image upload error:', error);
        return null;
      }

      // Generate Public URL
      const { data: publicData } = supabase.storage
        .from(bucket)
        .getPublicUrl(data.path);

      console.log(`✅ Image uploaded successfully: ${publicData.publicUrl}`);
      return publicData.publicUrl;
    } catch (error) {
      console.error('❌ Image upload failed:', error);
      return null;
    }
  },

  // Delete image
  deleteImage: async (imageUrl: string, bucket: string = 'product-images'): Promise<boolean> => {
    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      
      const { error } = await supabase.storage
        .from(bucket)
        .remove([fileName]);

      if (error) {
        console.error('❌ Image delete error:', error);
        return false;
      }

      console.log(`✅ Image deleted successfully: ${fileName}`);
      return true;
    } catch (error) {
      console.error('❌ Image delete failed:', error);
      return false;
    }
  },

  // Helper function to convert local path to Supabase URL
  convertLocalPathToSupabaseUrl: (localPath: string): string => {
    if (!localPath || localPath.startsWith('http')) {
      return localPath; // Return as-is if already a full URL
    }
    
    // Local path example: "/images/products/ED_MEDS/duratia-90-mg-1000x1000.png"
    // Convert to Supabase URL (using default bucket)
    const fileName = localPath.split('/').pop(); // Extract filename only
    const { data } = supabase.storage
      .from('product-images')
      .getPublicUrl(`legacy/${fileName}`);
    
    return data.publicUrl;
  },

  // Check if image URL is valid
  isValidImageUrl: (url: string): boolean => {
    if (!url) return true; // Allow empty (optional)

    try {
      // Check Supabase Storage URL pattern
      if (url.includes('supabase') && url.includes('storage/v1/object/public/')) {
        return true;
      }

      // Check general image URL
      const urlObj = new URL(url);
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(urlObj.pathname);
    } catch {
      return false;
    }
  }
};

// Points API
// Old pointApi removed - replaced with new user_points table-based system (line 2621)

// Coupon API
export const couponApi = {
  // Validate coupon
  validate: async (couponCode: string, userId: string, orderAmount: number) => {
    try {
      // 1. Look up coupon (case-insensitive). maybeSingle → 0 rows return null
      // (typo / nonexistent code) instead of PostgREST 406, which the auto-apply
      // loop would otherwise throw on every active coupon check.
      const normalizedCode = couponCode.toUpperCase();
      const { data: coupon, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', normalizedCode)
        .eq('is_active', true)
        .maybeSingle();

      if (error || !coupon) {
        return { valid: false, message: 'Invalid coupon code.' };
      }

      // 2. Check coupon ownership (security required!). 0 rows = user doesn't
      // own this coupon = a normal flow (not an error), so maybeSingle.
      const { data: userCoupon, error: userCouponError } = await supabase
        .from('user_coupons')
        .select('*')
        .eq('user_id', userId)
        .eq('coupon_id', coupon.id)
        .eq('is_used', false)
        .maybeSingle();

      if (userCouponError || !userCoupon) {
        return { valid: false, message: 'You do not own this coupon.' };
      }

      // 3. Check validity period
      const now = new Date();
      if (coupon.start_date && new Date(coupon.start_date) > now) {
        return { valid: false, message: 'This coupon is not yet active.' };
      }
      if (coupon.end_date && new Date(coupon.end_date) < now) {
        return { valid: false, message: 'This coupon has expired.' };
      }

      // 4. Check minimum amount
      if (orderAmount < (coupon.min_amount || 0)) {
        return {
          valid: false,
          message: `Minimum order of $${(coupon.min_amount || 0).toLocaleString()} required.`
        };
      }

      // 5. Check per-user usage count (count is_used=true in user_coupons)
      if (coupon.usage_per_user) {
        const { count } = await supabase
          .from('user_coupons')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('coupon_id', coupon.id)
          .eq('is_used', true);

        if (count && count >= coupon.usage_per_user) {
          return { valid: false, message: 'This coupon has already been used.' };
        }
      }

      // 6. Special condition validation (coupon-specific custom logic)
      // 6-1. New member exclusive coupon (WELCOME10)
      // 30-day limit removed (2026-02-09)

      // 6-2. Second purchase customer exclusive coupon (SECOND10000)
      if (coupon.code === 'SECOND10000') {
        const { count: orderCount } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .in('status', ['payment_confirmed', 'shipping', 'delivered']); // Only completed orders

        // Only users with exactly 1 purchase (this is their second purchase)
        if (orderCount !== 1) {
          return { valid: false, message: 'This coupon is for second-time buyers only.' };
        }
      }

      // 7. Calculate discount amount
      let discountAmount = 0;
      if (coupon.discount_type === 'percentage') {
        discountAmount = orderAmount * coupon.discount_value;
        if (coupon.max_discount) {
          discountAmount = Math.min(discountAmount, coupon.max_discount);
        }
      } else {
        discountAmount = coupon.discount_value;
      }

      return {
        valid: true,
        coupon: coupon,
        discountAmount: Math.floor(discountAmount)
      };
    } catch (error) {
      console.error('Coupon validation failed:', error);
      return { valid: false, message: 'An error occurred during coupon validation.' };
    }
  },

  // Claim coupon (called before order creation — flips is_used false→true).
  // Delegates to claim_user_coupon RPC which does the atomic UPDATE with a
  // is_used=FALSE predicate and rejects re-flips after order binding.
  claimCoupon: async (_userId: string, couponId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('claim_user_coupon', {
        p_coupon_id: couponId,
      });
      if (error) {
        console.error('Coupon claim RPC error:', error);
        return false;
      }
      const ok = !!data && (data as any).success === true;
      if (ok) console.log(`Coupon claimed: ${couponId}`);
      else console.error('Coupon claim rejected:', (data as any)?.message);
      return ok;
    } catch (error) {
      console.error('Coupon claim error:', error);
      return false;
    }
  },

  // Release coupon claim (rollback on order creation failure).
  // RPC enforces that release is only allowed while order_id IS NULL.
  releaseCoupon: async (_userId: string, couponId: string): Promise<void> => {
    try {
      const { data, error } = await supabase.rpc('release_user_coupon', {
        p_coupon_id: couponId,
      });
      if (error) {
        console.error('Coupon release RPC error:', error);
        return;
      }
      if ((data as any)?.success) {
        console.log(`Coupon claim released: ${couponId}`);
      } else {
        console.warn('Coupon release skipped:', (data as any)?.message);
      }
    } catch (error) {
      console.error('Coupon claim release failed:', error);
    }
  },

  // Record coupon usage (called after successful order creation — binds order_id).
  recordUsage: async (_userId: string, couponId: string, orderId: string, discountAmount: number) => {
    try {
      const { data, error } = await supabase.rpc('record_coupon_usage', {
        p_coupon_id: couponId,
        p_order_id: orderId,
      });
      if (error) throw error;
      if (!(data as any)?.success) {
        throw new Error((data as any)?.message || 'record_coupon_usage failed');
      }
      console.log(`Coupon usage recorded: ${couponId} (discount: $${discountAmount})`);
    } catch (error) {
      console.error('Coupon usage recording failed:', error);
      throw error;
    }
  },

  // Get all coupons (admin)
  getAll: async () => {
    try {
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Coupon fetch failed:', error);
      return [];
    }
  },

  // Get active coupons only (user)
  getActive: async () => {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('is_active', true)
        .or(`start_date.is.null,start_date.lte.${now}`)
        .or(`end_date.is.null,end_date.gte.${now}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Active coupon fetch failed:', error);
      return [];
    }
  },

  // Create coupon (admin)
  create: async (couponData: any) => {
    try {
      const { data, error } = await supabase
        .from('coupons')
        .insert(couponData)
        .select()
        .single();

      if (error) throw error;
      console.log(`Coupon created: ${couponData.code}`);
      return data;
    } catch (error) {
      console.error('Coupon creation failed:', error);
      throw error;
    }
  },

  // Update coupon (admin)
  update: async (couponId: string, updates: any) => {
    try {
      const { data, error } = await supabase
        .from('coupons')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', couponId)
        .select()
        .single();

      if (error) throw error;
      console.log(`Coupon updated: ${couponId}`);
      return data;
    } catch (error) {
      console.error('Coupon update failed:', error);
      throw error;
    }
  },

  // Delete coupon (admin)
  delete: async (couponId: string) => {
    try {
      const { error } = await supabase
        .from('coupons')
        .delete()
        .eq('id', couponId);

      if (error) throw error;
      console.log(`Coupon deleted: ${couponId}`);
    } catch (error) {
      console.error('Coupon deletion failed:', error);
      throw error;
    }
  },

  // Auto-find best coupon
  getBestCoupon: async (userId: string, orderAmount: number) => {
    try {
      console.log('Searching for best coupon...', { userId, orderAmount });

      // 1. Get all active coupons
      const { data: allCoupons, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('is_active', true);

      if (error || !allCoupons || allCoupons.length === 0) {
        console.log('No available coupons.');
        return { bestCoupon: null, maxDiscount: 0, validCoupons: [] };
      }

      // 2. Validate each coupon and calculate discount
      const validationResults = await Promise.all(
        allCoupons.map(async (coupon: Coupon) => {
          const validation = await couponApi.validate(coupon.code, userId, orderAmount);
          return {
            coupon,
            isValid: validation.valid,
            discountAmount: validation.valid ? validation.discountAmount : 0,
            message: validation.message
          };
        })
      );

      // 3. Filter valid coupons only
      const validCoupons = validationResults.filter(result => result.isValid);

      if (validCoupons.length === 0) {
        console.log('No coupons currently available.');
        return { bestCoupon: null, maxDiscount: 0, validCoupons: [] };
      }

      // 4. Find coupon with maximum discount
      const bestResult = validCoupons.reduce((best, current) => {
        return (current.discountAmount || 0) > (best.discountAmount || 0) ? current : best;
      });

      console.log('Best coupon found:', {
        code: bestResult.coupon.code,
        discount: bestResult.discountAmount
      });

      return {
        bestCoupon: bestResult.coupon,
        maxDiscount: bestResult.discountAmount || 0,
        validCoupons: validCoupons.map(r => ({
          code: r.coupon.code,
          name: r.coupon.name,
          discount: r.discountAmount
        }))
      };
    } catch (error) {
      console.error('Best coupon search failed:', error);
      return { bestCoupon: null, maxDiscount: 0, validCoupons: [] };
    }
  },

  // Get user coupons
  getUserCoupons: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_coupons')
        .select(`
          id,
          coupon_id,
          is_used,
          used_at,
          order_id,
          created_at,
          coupons (
            id,
            code,
            name,
            discount_type,
            discount_value,
            min_amount,
            max_discount,
            end_date,
            is_active
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('User coupon fetch failed:', error);
      return [];
    }
  }
};

// Referral (friend invite) API
export const referralApi = {
  // Create or get referral link
  getOrCreateReferralLink: async (userId: string, userName: string) => {
    try {
      // 1. Check existing link
      const { data: existing, error: fetchError } = await supabase
        .from('referral_links')
        .select('*')
        .eq('referrer_id', userId)
        .maybeSingle();

      if (existing && !fetchError) {
        return existing;
      }

      // 2. Generate new link (6 chars, excluding confusing characters)
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excludes 0,O,1,I
      const referralCode = Array.from({length: 6}, () =>
        chars[Math.floor(Math.random() * chars.length)]
      ).join('');

      const { data: newLink, error: createError } = await supabase
        .from('referral_links')
        .insert({
          referrer_id: userId,
          referral_code: referralCode
        })
        .select()
        .single();

      if (createError) throw createError;
      console.log(`Referral link created: ${referralCode}`);
      return newLink;
    } catch (error) {
      console.error('Referral link create/fetch failed:', error);
      throw error;
    }
  },

  // Process signup via referral code (called on new signup).
  // Delegates to register_referral_signup RPC (SECURITY DEFINER) — the RPC
  // atomically validates the code, creates the signup row, grants the
  // welcome coupon, and bumps the referrer's signups counter without
  // needing permissive RLS on cross-user writes.
  registerReferralSignup: async (referralCode: string, newUserId: string) => {
    try {
      const { data, error } = await supabase.rpc('register_referral_signup', {
        p_referral_code: referralCode,
        p_new_user_id: newUserId,
      });

      if (error) {
        console.error('Referral signup RPC error:', error);
        return null;
      }
      if (!data || (data as any).success !== true) {
        console.log('Referral signup rejected:', (data as any)?.message);
        return null;
      }

      console.log(`New signup coupon issued: ${newUserId} - ${(data as any).referee_coupon_code}`);
      return data;
    } catch (error) {
      console.error('Referral signup processing failed:', error);
      return null;
    }
  },

  // Reward referrer on friend's first purchase.
  // Delegates to reward_referrer_on_purchase RPC (SECURITY DEFINER) —
  // grants points to the referrer, records the reward, and bumps the
  // conversions counter atomically.
  rewardReferrerOnFirstPurchase: async (newUserId: string, _orderId: string) => {
    try {
      const { data, error } = await supabase.rpc('reward_referrer_on_purchase', {
        p_new_user_id: newUserId,
        p_order_id: _orderId,
      });

      if (error) {
        console.error('Referral reward RPC error:', error);
        return false;
      }
      const success = !!data && (data as any).success === true;
      if (success) {
        console.log(`Referrer points granted: $${(data as any).points_granted}`);
      } else {
        console.log('Referral reward skipped:', (data as any)?.message);
      }
      return success;
    } catch (error) {
      console.error('Referral reward failed:', error);
      return false;
    }
  },

  // Get my referral stats
  getMyReferralStats: async (userId: string) => {
    try {
      const { data: link } = await supabase
        .from('referral_links')
        .select('*')
        .eq('referrer_id', userId)
        .single();

      // Get basic signup data first
      const { data: signups, error: signupsError } = await supabase
        .from('referral_signups')
        .select('*')
        .eq('referrer_id', userId)
        .order('created_at', { ascending: false });

      if (signupsError) {
        console.error('referral_signups fetch failed:', signupsError);
      }

      // Fetch friend profile info separately (basic data retained on failure)
      let enrichedSignups = signups || [];
      if (enrichedSignups.length > 0) {
        const refereeIds = enrichedSignups.map((s: any) => s.referee_id).filter(Boolean);
        if (refereeIds.length > 0) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, name, phone')
            .in('id', refereeIds);

          if (profiles && profiles.length > 0) {
            const profileMap = new Map(profiles.map((p: any) => [p.id, p]));
            enrichedSignups = enrichedSignups.map((s: any) => ({
              ...s,
              referee: profileMap.get(s.referee_id) || null
            }));
          }
        }
      }

      const { data: rewards } = await supabase
        .from('referral_rewards')
        .select('*')
        .eq('user_id', userId);

      return {
        link: link || null,
        totalSignups: enrichedSignups.length || 0,
        totalConversions: enrichedSignups.filter((s: any) => s.first_purchase_at).length,
        totalRewards: rewards?.length || 0,
        recentSignups: enrichedSignups.slice(0, 10) || [],
        recentRewards: rewards?.slice(0, 10) || []
      };
    } catch (error) {
      console.error('Referral stats fetch failed:', error);
      return null;
    }
  }
};


// Points API
export const pointApi = {
  // Get points balance
  getBalance: async (userId: string): Promise<number> => {
    try {
      const { data, error } = await supabase
        .from('user_points')
        .select('balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      return data?.balance || 0;
    } catch (error) {
      console.error('Points balance fetch failed:', error);
      return 0;
    }
  },

  // Add points (referral reward, admin grant, review reward, cancel refund, etc).
  // The 5th arg is the related order_id when applicable — propagated to
  // point_transactions.order_id so the audit trail links back to the source order.
  addPoints: async (
    userId: string,
    amount: number,
    type: string, // 'referral_reward' | 'admin_grant' | 'order_cancel_refund' | 'review_reward' | etc
    description: string,
    orderId?: string
  ): Promise<boolean> => {
    try {
      devLog.info('Adding points:', { userId, amount, type, description, orderId });

      // Call Database Function (bypasses RLS, safe point grant)
      const { data, error } = await supabase.rpc('grant_review_points', {
        p_user_id: userId,
        p_amount: amount,
        p_type: type,
        p_description: description,
        p_order_id: orderId ?? null,
      });

      if (error) {
        console.error('Points grant failed:', error);
        return false;
      }

      if (data === true) {
        devLog.info('Points added successfully:', { userId, amount, type });
        return true;
      } else {
        console.error('Points grant failed: Function returned false');
        return false;
      }
    } catch (error) {
      console.error('Points addition failed:', error);
      return false;
    }
  },

  // Spend points on an order. Delegates to spend_points RPC which
  // atomically checks balance >= amount inside a single UPDATE, so
  // overdraw is impossible even under concurrent calls. Always
  // spends from auth.uid(); the userId arg is preserved for call-site
  // compatibility and asserted against the caller implicitly.
  usePoints: async (
    _userId: string,
    amount: number,
    orderId: string,
    description: string
  ): Promise<boolean> => {
    try {
      devLog.info('Using points:', { amount, orderId });
      const { data, error } = await supabase.rpc('spend_points', {
        p_amount: amount,
        p_order_id: orderId,
        p_description: description,
      });
      if (error) {
        console.error('spend_points RPC error:', error);
        return false;
      }
      if (!data || (data as any).success !== true) {
        console.error('Points usage rejected:', (data as any)?.message);
        return false;
      }
      devLog.info('Points used successfully:', { amount, newBalance: (data as any).new_balance });
      return true;
    } catch (error) {
      console.error('Points usage failed:', error);
      return false;
    }
  },

  // Get transaction history
  getTransactions: async (userId: string, limit: number = 50): Promise<any[]> => {
    try {
      const { data, error } = await supabase
        .from('point_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Transaction history fetch failed:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Transaction history fetch failed:', error);
      return [];
    }
  }
};
